import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs/Rx';
import 'rxjs/add/operator/mergeMap';
import * as moment from 'moment';

import { environment } from '../../environments/environment';
import { AsperaService } from '../aspera/aspera.service';
import { AuthService } from '../auth/auth.service';
import { Transfer } from './transfer.model';
import { TransferFile } from './transfer-file.model';
import { User } from '../users/user.model';

@Injectable()
export class TransfersService {

  apiHost = environment.apiHost;
  currentUser: User = this.authService.currentUser;
  get signedIn(): boolean {
    return this.authService.signedIn;
  }

  constructor(
    private http: HttpClient,
    private asperaService: AsperaService,
    private authService: AuthService) {}

  createToken(direction: string, uploadPackageGuid: string, uploadPackageId: number, downloadPackageId: number, fileIds: number[]): Observable<any> {
    const payload = {
      direction: direction,
      upload_package_guid: uploadPackageGuid,
      upload_package_id: uploadPackageId,
      download_package_id: downloadPackageId,
      file_ids: fileIds
    };
    return this.http.post(this.apiHost + '/token', payload).map(
      (data: any) => {
        const transferSpec = data.transfer_specs[0].transfer_spec;
        return transferSpec;
      },
      (errors) => {console.log('Create token error:'); console.log(errors); }
    );
  }

  transferFiles(direction: string, uploadPackageGuid: string, uploadPackageId: number, files: TransferFile[]): Observable<any> {
    let observable = Observable.from([null]);
    const fileIds = files.map((f) => +f.id);
    let downloadPackageId : number;

    if (direction === 'download') {
      // Workaround for createDownload call timing out if we give it too many ids at once
      const batchSize = 20;
      let i = 0;
      while (i < fileIds.length) {
        const batch = fileIds.slice(i, i + batchSize);
        observable = observable.flatMap(() => {
          return this.createDownload(batch);
        });
        i += batchSize;
      }
    }

    return observable.flatMap((download_package_id) => {
      if (download_package_id != null) {
        downloadPackageId = download_package_id;
      }

      return this.createToken(
        direction,
        uploadPackageGuid,
        uploadPackageId,
        downloadPackageId,
        fileIds
      ).flatMap((spec) => {
        return Observable.fromPromise(
          this.asperaService.transferFiles(spec)
        ).map(
          (transfer: any) => {
            return {
              requestId: transfer.uuid,
              cookie: transfer.transfer_spec.cookie
            };
          },
          (error) => console.log('Transfer error: ', error)
        );
      });
    });
  }

  createPackage(transfer: Transfer): Observable<any> {
    const path = '/packages';
    const payload = this.generatePackagePayload(transfer);
    return this.http.post(this.apiHost + path, payload).map(
      (data: any) => {
        return this.parsePackage(data);
      }
    ).catch((error) => {
        console.log('Create package error:'); console.log(error);
        if (error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(error.status);
      }
    );
  }

  getPackage(packageId: string): Observable<any> {
    const path = '/packages/' + packageId;
    return this.http.get(this.apiHost + path).map(
      (data: any) => {
        const transfer = Transfer.fromDeliveryRecord(data.deliveryHistory);
        return transfer;
      }
    ).catch((error) => {
        console.log('Get package error:'); console.log(error);
        if (this.signedIn && error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(error.status);
      }
    );
  }

  submitForm(transfer: Transfer): Observable<any> {
    const path = 'form/' + transfer.workflowId;
    const payload = this.generatePackagePayload(transfer);

    return this.http.post(this.apiHost + '/' + path, payload).map(
      (data: any) => {
        return data;
      }
    ).catch((error) => {
        console.log('Submit form error:'); console.log(error);
        if (error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(error.status);
      }
    );
  }

  generatePackagePayload(transfer: Transfer) {
    const metadata = [];
    for (const key in transfer.metadata) {
      const value = transfer.metadata[key];
      metadata.push({
        field_id: key,
        field_values: value
      });
    }

    const files = [];
    const folders = [];
    for (const file of transfer.files) {
      if (file.type === 'inode/directory') {
        folders.push(file);
      } else {
        files.push(file);
      }
    }

    const payload: any = {
      delivery_notes: transfer.notes,
      recipients: transfer.recipients.map((r) => {
        return {
          user_id: r.id != null ? r.id : '',
          user_email: r.email != null ? r.email : '',
          user_first: r.firstName != null ? r.firstName : '',
          user_last: r.lastName != null ? r.lastName : ''
        };
      }),
      files: files.map((file) => {
        return {
          filename: file.name,
          filesize: file.size,
          source_relative_path: file.path
        };
      }),
      notifications: {
        list: transfer.notifications.map((email) => {
          return {mail: email};
        })
      },
      md_obj: {metadata_object: {fields: metadata}},
      wf_id: transfer.workflowId,
      wf_type: transfer.workflowType,
      template_id: transfer.templateId,
      priority: transfer.priority
    };

    if (folders.length > 0) {
      payload.folders = folders.map((folder) => {
        return {
          filename: folder.name,
          filesize: folder.size,
          source_relative_path: folder.path
        };
      });
    }

    if (transfer.expirationDate != null && transfer.expirationDate.isValid()) {
      const exp = moment(transfer.expirationDate);
      exp.add(1, 'day');
      payload.expiration_date = exp.format('YYYY-MM-DD');
    }

    const currentEmail = this.currentUser.email.toLowerCase();
    const included = payload.notifications.list.find((n) => n.mail.toLowerCase() == currentEmail);
    if (included == null) {
      payload.notifications.list.push({mail: this.currentUser.email});
    }

    return payload;
  }

  createDownload(dhIds: number[]): Observable<any> {
    const payload = {
      dh_ids: dhIds
    };
    let downloadPackageId;
    return this.http.post(this.apiHost + '/downloads', payload).map(
      (data: any) => {
        if (data != null && data.delivery != null) {
          downloadPackageId = data.package_id;
        }
        return downloadPackageId;
    }).catch((error) => {
      console.log('Update download status error:'); console.log(error);
      if (this.signedIn && error.status === 401) {
        this.authService.signIn();
      }
      return Observable.throw(error.status);
    });
  }

  extendExpiration(transfer: Transfer, expiration: string) {
    // API changes expiration to end of previous day
    // Workaround that by adding a day to desired date
    const expirationMoment = moment(expiration);
    if (expirationMoment.isValid()) {
      const nextDay = expirationMoment.add(1, 'days');
      expiration = nextDay.format('YYYY-MM-DD');
    } else {
      expiration = null;
    }

    let observable;
    for (const file of transfer.files) {
      const o = this.extendDeliveryExpiration(file.id, expiration);
      if (observable == null) {
        observable = o;
      } else {
        observable = observable.flatMap(() => o);
      }
    }
    return observable;
  }

  extendDeliveryExpiration(deliveryId: string, expiration: string) {
    const payload = {
      user_id: this.currentUser.id,
      delivery_id: deliveryId,
      new_expiration_date: expiration
    };
    return this.http.post(this.apiHost + '/setnewexpirationdate', payload).map(
      (data) => data
    ).catch((error) => {
        console.log('Extend expiration status error:'); console.log(error);
        if (this.signedIn && error.status === 401) {
          this.authService.signIn();
        }
        return Observable.throw(error.status);
      }
    );
  }

  parsePackage(data) {
    const packageId = data.package_id;
    const packageGuid = data.package_guid;
    const deliveryDate = moment(data.delivery_date, 'YYYY-MM-DD hh:mm:ss');
    const files = data.delivery.map((f) => {
      const file = new TransferFile;
      file.corrId = f.corr_id;
      file.id = '' + f.delivery_history_id;
      file.name = f.filename;
      file.path = f.source_relative_path;
      file.size = f.filesize;
      return file;
    });
    return {packageId: packageId, packageGuid: packageGuid, deliveryDate: deliveryDate, files: files};
  }

}



// WEBPACK FOOTER //
// ./src/app/transfers/transfers.service.ts